import { Dispatch, SetStateAction, useState } from "react";
import { Box, Button, CircularProgress, Divider, Drawer, IconButton, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { clientInitialPermissions, preview_image } from "@/constants";
import { HeadingText, StyledBox, StyledTextInput } from "./styles";
import SwitchButton from "@/components/switch";
import { PartnerPicker, PictureUploader } from "@/components";
import HierarchicalCheckbox from "@/components/checkbox/hierarchical";
import { PAGE_OPTIONS, filterOptionsByAvailable, normalizeForStorage, collectAllKeys } from "@/roles/protection/pageOptions";
import { useAddClientMutation } from "@/services";
import { roles } from "@/roles";
import { useGetTenantAllowedPagesQuery, useSetUserAllowedPagesMutation } from "@/graphql/page-access";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSnackbar, useUploadFile } from "@/hooks";
import { ValidationPatterns } from "@/utils/form-validation";
import { useAppDispatch, useAppSelector } from "@/store";
import { clientApi } from "@/services/client/client-api";
import { tokens } from "@/tokens";

type AddClientModalProps = {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  selectedPartner: SPOwnerPartnerTypes | null;
  setSelectedPartner: Dispatch<SetStateAction<SPOwnerPartnerTypes | null>>;
};

const AddClientModal = ({
  show,
  setShow,
  selectedPartner,
  setSelectedPartner,
}: AddClientModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ClientFormValues>({
    defaultValues: {
      firstName: "",
      email: "",
      profileImage: null,
      assignedPartner: "",
    },
  });
  const [showPermissions, setShowPermissions] = useState(false);
  const [permissions, setPermissions] = useState(clientInitialPermissions);
  const [allowedPages, setAllowedPages] = useState<string[]>([]);
  const [profilePreview, setProfilePreview] = useState<string | undefined>(
    undefined
  );
  const { isLoading: profileImgLoading, uploadFile } = useUploadFile();
  const user = useAppSelector((state) => state.auth.user);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const isAdmin = user?.userRole === roles.TDADMIN;
  const isTDAdminSP = isAdmin && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(
    isTDAdminSP ? { tenantId: selectedTenant?._id } : undefined
  );
  const availablePageKeys = tenantPagesResp?.pages || [];
  const filteredOptions = filterOptionsByAvailable(PAGE_OPTIONS, availablePageKeys);
  const [setUserAllowedPages] = useSetUserAllowedPagesMutation();
  const handleTriggerPermissions = () => {
    setShowPermissions((prev) => !prev);
    setPermissions(clientInitialPermissions);
  };
  const { show: showSnackBar } = useSnackbar();

  const [addClient, { isLoading }] = useAddClientMutation();

  const handleProfileChange = (file: File | null) => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setValue("profileImage", file);
      setProfilePreview(fileURL);
    }
  };

  const dispatch = useAppDispatch();
  const handleCreateClient: SubmitHandler<ClientFormValues> = async (data) => {
    const { firstName, assignedPartner, profileImage, email } = data;

    let profileImgKey;
    if (profileImage) {
      const data = await uploadFile(profileImage);
      profileImgKey = data?.key || "";
    }
    const clientData: ClientType = {
      firstName,
      email,
      profileImage: profileImgKey || "",
      isRestricted: !showPermissions,
      assignedPartner: assignedPartner ? assignedPartner : null,
      ...(isAdmin && { tenantId: selectedTenant?._id }),
      clientPermission: {
        dashboard: Object.keys(permissions.dashboard).filter(
          (key) =>
            permissions.dashboard[key as keyof typeof permissions.dashboard]
        ),
        documents: permissions.documents,
        reports: Object.keys(permissions.reports).filter(
          (key) => permissions.reports[key as keyof typeof permissions.reports]
        ),
      } as ClientAccessLevelPermissions,
    };

    try {
      await addClient({
        clientData,
      }).unwrap();
      const validKeys = collectAllKeys(filteredOptions);
      const finalPages = normalizeForStorage((allowedPages || []).filter((k) => validKeys.has(k)), PAGE_OPTIONS);
      if (finalPages.length > 0) {
        try {
          const resp = await dispatch(
            clientApi.endpoints.getAllClients.initiate({
              pageNo: 1,
              pageSize: 10,
              searchKeyword: email,
              ...(isAdmin && { tenantId: selectedTenant?._id }),
            })
          ).unwrap();
          const newClient = resp?.data?.find((c) => c.email === email);
          if (newClient?._id) {
            await setUserAllowedPages({ userId: newClient._id, pages: finalPages }).unwrap();
          }
        } catch (e) {
          try {
            showSnackBar("Client created, but assigning pages failed. You can update access in Settings.", "", "warning");
          } catch {}
        }
      }
      setSelectedPartner(null);
      showSnackBar("Client Created Successfully!", "", "success");
      setShow(false);
      reset();
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = error?.message;
        showSnackBar(errorMessage, "", "error");
      }
    }
  };

  const isSubmitting = isLoading || profileImgLoading;

  return (
    <Drawer
      anchor="right"
      open={show}
      onClose={() => setShow(false)}
      PaperProps={{
        sx: {
          width: 480,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${tokens.border.default}`,
        bgcolor: tokens.bg.surface,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Box>
          <Typography sx={{ fontSize: tokens.fontSize.xl, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
            Add New Client
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.25 }}>
            They'll be able to access Riffs and see their data once invited
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setShow(false)} sx={{ color: tokens.text.muted }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: tokens.bg.muted, p: 3 }}>
        <Box component="form" onSubmit={handleSubmit(handleCreateClient)}>
          {/* Partner Picker Card */}
          <Box sx={{ bgcolor: tokens.bg.surface, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.border.default}`, p: 2.5, mb: 2.5 }}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Assign Partner
            </Typography>
            <Controller
              name="assignedPartner"
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <PartnerPicker
                  {...rest}
                  selectedPartner={selectedPartner}
                  setSelectedPartner={(partner: SPOwnerPartnerTypes | null) => {
                    const partnerId = partner ? partner._id : "";
                    onChange(partnerId);
                    setSelectedPartner(partner);
                  }}
                  error={errors.assignedPartner}
                />
              )}
            />
            {errors.assignedPartner && (
              <Typography
                color="error"
                sx={{
                  mt: 0.5,
                  fontSize: tokens.fontSize.base,
                  ml: 1,
                }}
              >
                {errors.assignedPartner.message}
              </Typography>
            )}
          </Box>

          {/* Logo Upload Card */}
          <Box sx={{ bgcolor: tokens.bg.surface, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.border.default}`, p: 2.5, mb: 2.5 }}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Logo
            </Typography>
            <PictureUploader
              title=""
              description="We recommend an image of at least 400x400"
              imgUrl={profilePreview ? profilePreview : preview_image}
              customPreviewType={profilePreview ? "rounded" : "default"}
              onChange={handleProfileChange}
            />
          </Box>

          {/* Business Info Card */}
          <Box sx={{ bgcolor: tokens.bg.surface, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.border.default}`, p: 2.5, mb: 2.5 }}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Business Info
            </Typography>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: "Business name is required" }}
              render={({ field: { onChange, ...rest }, fieldState }) => (
                <StyledTextInput
                  label="Business Name"
                  type="text"
                  {...rest}
                  placeholder="Business Name"
                  handleChangeValue={onChange}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required",
                pattern: {
                  value: ValidationPatterns.email,
                  message: "Invalid email address",
                },
              }}
              render={({ field: { onChange, ...rest }, fieldState }) => (
                <StyledTextInput
                  label="Email Address"
                  {...rest}
                  type="text"
                  placeholder="h1@example.com"
                  handleChangeValue={onChange}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>

          {/* Login Permissions Card */}
          <Box sx={{ bgcolor: tokens.bg.surface, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.border.default}`, overflow: 'hidden' }}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, p: 2.5, pb: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Login Permissions
            </Typography>
            <StyledBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <HeadingText>
                  Do you want your client to login to the system
                </HeadingText>
                <Typography
                  sx={{
                    color: tokens.text.muted,
                    fontSize: tokens.fontSize.base,
                  }}
                >
                  This will provide your client a portal to login and manage
                  everything
                </Typography>
              </Box>
              <SwitchButton label="" onChange={handleTriggerPermissions} />
            </StyledBox>
            {showPermissions && (
              <>
                <Divider />
                <StyledBox>
                  <HeadingText>Pages</HeadingText>
                  <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base }}>
                    Toggle which app pages this client can access
                  </Typography>
                </StyledBox>
                <StyledBox>
                  <HierarchicalCheckbox
                    options={filteredOptions}
                    selectedKeys={allowedPages}
                    onChange={setAllowedPages}
                  />
                  {filteredOptions.length === 0 && (
                    <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base, mt: 0.5 }}>
                      No tenant pages configured. Contact your administrator.
                    </Typography>
                  )}
                </StyledBox>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 3,
        py: 2,
        borderTop: `1px solid ${tokens.border.default}`,
        bgcolor: tokens.bg.surface,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
      }}>
        <Button
          variant="outlined"
          onClick={() => setShow(false)}
          disabled={isSubmitting}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 38,
            px: 2.5,
            '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleCreateClient)}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: tokens.bg.surface }} /> : undefined}
          sx={{
            bgcolor: tokens.brand.primary,
            color: tokens.bg.surface,
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 38,
            px: 2.5,
            boxShadow: 'none',
            '&:hover': { bgcolor: tokens.brand.primaryHover, boxShadow: 'none' },
          }}
        >
          Invite
        </Button>
      </Box>
    </Drawer>
  );
};

export default AddClientModal;
